import React, { useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { getQRCode, getSession, getDeviceHealth, getProviderInfo, getNumberHealth, getWhatsAppDisclaimer, ackWhatsAppDisclaimer, getWzapNotifySettings, setWzapNotifySettings } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { Switch } from '../components/ui/switch';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { Checkbox } from '../components/ui/checkbox';
import { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider } from '../components/ui/tooltip';
import { Popover, PopoverTrigger, PopoverContent } from '../components/ui/popover';
import { RefreshCw, CheckCircle, XCircle, Loader2, QrCode, Smartphone, Clock, RotateCw, Lightbulb, ExternalLink, HelpCircle, Bell, CloudOff } from 'lucide-react';

// Faíscas que saem da PONTA da barra durante a animação de entrada (offsets em px em torno da ponta).
const TIP_SPARKS = [
  { dx: -4, size: 5, delay: '0s',    color: '#86efac' },
  { dx:  3, size: 4, delay: '0.12s', color: '#ffffff' },
  { dx:  6, size: 6, delay: '0.28s', color: '#34d399' },
  { dx: -6, size: 4, delay: '0.40s', color: '#bbf7d0' },
  { dx:  1, size: 5, delay: '0.55s', color: '#22c55e' },
  { dx:  5, size: 4, delay: '0.70s', color: '#ffffff' },
];

// Dica de boas práticas acima do QR: recomenda WhatsApp Business + número próprio/aquecido.
// Tom acolhedor (não é alarme). Enquanto a ciência não é confirmada (acked===false) mostra o
// checkbox + botão que registra a ciência no servidor; depois (acked===true) fica só a dica.
// Cobertura legal (cláusula 6.9) fica no "Saiba mais"/checkbox — já aceita no cadastro; aqui é reforço.
function ConnectDisclaimer({ acked, acking, onAck }) {
  const [checked, setChecked] = useState(false);
  return (
    <div className="w-full rounded-lg border border-sky-200 bg-sky-50 dark:border-sky-800/50 dark:bg-sky-900/20 p-3 text-sm">
      <div className="flex gap-2">
        <Lightbulb className="w-4 h-4 text-sky-600 dark:text-sky-400 shrink-0 mt-0.5" />
        <div className="text-sky-800 dark:text-sky-200 min-w-0">
          <p>
            <strong>Dica para uma conexão saudável:</strong> conecte um número próprio e já usado no
            dia a dia, de preferência pelo app <strong>WhatsApp Business</strong>. Assim sua conta chega
            mais preparada para os envios. 😊
          </p>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className="mt-1 text-xs font-medium text-sky-700 dark:text-sky-300 hover:underline"
              >
                Saiba mais
              </button>
            </PopoverTrigger>
            <PopoverContent
              align="start"
              sideOffset={6}
              className="w-80 max-w-[calc(100vw-2rem)] border-sky-200 bg-sky-50 dark:border-sky-800/50 dark:bg-sky-900/20 p-3"
            >
              <p className="text-xs leading-relaxed text-sky-800/90 dark:text-sky-200/90">
                O Watizap se conecta ao seu WhatsApp por QR Code, como o WhatsApp Web. Para melhores
                resultados, use uma conta <strong>WhatsApp Business</strong> com perfil completo, em número próprio, e
                aumente o volume aos poucos nos primeiros dias: isso ajuda a manter a conta saudável.
                As regras de uso são definidas pela Meta, então a escolha do número e do tipo de conta
                fica a seu critério, nos termos da{' '}
                <a
                  href="/termos-de-uso"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-0.5 font-medium underline"
                >
                  cláusula 6.9 dos Termos de Uso <ExternalLink className="w-3 h-3" />
                </a>
              </p>
            </PopoverContent>
          </Popover>
          {acked === false && (
            <>
              <label className="mt-3 flex items-center gap-2 cursor-pointer">
                <Checkbox
                  checked={checked}
                  onCheckedChange={(v) => setChecked(!!v)}
                  disabled={acking}
                  className="shrink-0 border-sky-500"
                />
                <span className="text-[13px] text-sky-800 dark:text-sky-200">
                  Li e entendo que a escolha do número e de conta é minha
                  (cláusula 6.9 dos Termos de Uso).
                </span>
              </label>
              <Button
                type="button"
                size="sm"
                className="mt-2 h-8"
                disabled={!checked || acking}
                onClick={onAck}
              >
                {acking ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                Confirmar e ver QR Code
              </Button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function WhatsAppPage() {
  const { isAdmin, user, refreshUser, updateUser } = useAuth();
  const [qrData, setQrData] = useState(null);
  const [session, setSession] = useState(null);
  const [health, setHealth] = useState(null);
  const [numberHealth, setNumberHealth] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [patConfigured, setPatConfigured] = useState(true);
  const [activationStartedAt, setActivationStartedAt] = useState(null);
  const [nowTs, setNowTs] = useState(Date.now());
  const [barW, setBarW] = useState(100); // largura (%) da barra — animada por JS na entrada
  const [sprinkling, setSprinkling] = useState(false);
  const prevConnected = useRef(null);
  // Ciência do aviso de conexão (recomenda WhatsApp Business): null=carregando, false=pendente, true=confirmada.
  const [disclaimerAcked, setDisclaimerAcked] = useState(null);
  const [acking, setAcking] = useState(false);
  // Alerta por e-mail quando o número desconecta (config do tenant, só o dono edita). Default ligado.
  const [backendDown, setBackendDown] = useState(false);
  const [notifyOnDisconnect, setNotifyOnDisconnect] = useState(true);
  const [notifySaving, setNotifySaving] = useState(false);

  const getStatus = (sessionData, healthData) => {
    const ss = sessionData?.status || '';
    const hs = healthData?.status || '';
    const connectedStates = ['online', 'connected', 'healthy'];
    const needsQRStates = ['authorize', 'not_configured', 'need_scan', 'qr', 'logged_out'];
    const connectingStates = ['initializing', 'connecting', 'pending', 'loading', 'authenticated', 'ready'];
    
    // Provedor mudo: pane NOSSA, não do número do cliente. Tem que vir ANTES dos demais, senão
    // cairia em "transitional" (qualquer status desconhecido) e a tela diria "Reconectando",
    // sugerindo que a conexão do usuário caiu.
    const providerDown = ss === 'provider_unreachable' || hs === 'provider_unreachable';

    const connected = !providerDown && (connectedStates.includes(ss) || connectedStates.includes(hs));
    const needsQR = !providerDown && !connected && (needsQRStates.includes(ss) || needsQRStates.includes(hs));
    // Transitional: known connecting states OR unknown non-error state
    const transitional = !providerDown && !connected && !needsQR && (connectingStates.includes(ss) || (ss !== '' && ss !== 'error'));

    return { connected, needsQR, transitional, providerDown, ss, hs };
  };

  const pollStatus = useCallback(async (isInitial = false) => {
    try {
      // Check PAT configuration first - if not configured, skip session/qr calls
      try {
        const provRes = await getProviderInfo();
        const hasPat = !!provRes.data?.pat_configured;
        setPatConfigured(hasPat);
        setActivationStartedAt(provRes.data?.created_at || null);
        if (!hasPat) {
          setQrData(null);
          setSession(null);
          setHealth(null);
          setBackendDown(false);
          return true;  // conta em ativação: a leitura DEU certo, não é falha
        }
      } catch {
        // ignore provider call failure - fall through
      }

      const [sessionRes, healthRes] = await Promise.all([
        getSession(),
        getDeviceHealth(),
      ]);
      setSession(sessionRes.data);
      setHealth(healthRes.data);

      const { connected, needsQR, transitional } = getStatus(sessionRes.data, healthRes.data);

      // Detect transitions
      if (!isInitial && prevConnected.current !== null) {
        if (connected && !prevConnected.current) {
          toast.success('WhatsApp conectado!');
        } else if (!connected && !transitional && prevConnected.current) {
          toast.error('WhatsApp desconectado');
        }
      }
      prevConnected.current = connected;

      // Primeira conexão da conta: o backend acabou de gravar o carimbo definitivo. Recarrega
      // o usuário para o balão do menu sumir na hora, sem a pessoa precisar dar F5.
      if (connected && user?.whatsapp_onboarding_pending) {
        refreshUser();
      }
      // Reconexão depois de uma queda: aqui NÃO adianta recarregar do servidor, porque o
      // carimbo da queda só é apagado pela varredura, que passa a cada 120s (e é ela quem
      // manda o e-mail de recuperação, então não dá para apagar antes). O balão some na hora
      // localmente e o servidor confirma no próximo /me: o balão é dica, não fonte da verdade.
      if (connected && user?.whatsapp_reconnect_pending) {
        updateUser({ ...user, whatsapp_reconnect_pending: false });
      }

      // Fetch QR only if we need scan (not during transitional/connecting)
      if (needsQR && !transitional) {
        try {
          const qrRes = await getQRCode();
          setQrData(qrRes.data);
        } catch {
          setQrData(null);
        }
      } else if (connected || transitional) {
        setQrData(null);
      }
      // Backend fora do ar conta como indisponibilidade também: a tela precisa dizer a mesma
      // coisa, em vez de voltar a mostrar "Offline" com dados velhos.
      setBackendDown(false);
      return !getStatus(sessionRes.data, healthRes.data).providerDown;
    } catch (error) {
      setBackendDown(true);
      if (isInitial) toast.error('Erro ao carregar dados do WhatsApp');
      return false;
    } finally {
      if (isInitial) setLoading(false);
    }
  }, []);

  // Initial load
  useEffect(() => { pollStatus(true); }, [pollStatus]);

  // Polling contínuo: 5s no normal. Enquanto o provedor (ou o backend) não responde, o
  // intervalo dobra até 20s — sem isso a tela seguia perguntando a cada 5s contra um servidor
  // mudo, empilhando pedidos. Volta a 5s no primeiro sucesso.
  useEffect(() => {
    let cancelado = false;
    let id;
    const agendar = (atraso) => {
      id = setTimeout(async () => {
        const ok = await pollStatus(false);
        if (cancelado) return;
        agendar(ok ? 5000 : Math.min(20000, atraso * 2));
      }, atraso);
    };
    agendar(5000);
    return () => { cancelado = true; clearTimeout(id); };
  }, [pollStatus]);

  // Saúde do número (conexão + sucesso de envio 24h): a cada 30s, independente do poll de 5s.
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const res = await getNumberHealth();
        if (alive) setNumberHealth(res.data);
      } catch { /* silencioso — o card só some */ }
    };
    load();
    const id = setInterval(load, 30000);
    return () => { alive = false; clearInterval(id); };
  }, []);

  // Ciência do aviso de conexão: busca UMA vez na montagem (o QR fica travado até confirmar).
  useEffect(() => {
    let alive = true;
    getWhatsAppDisclaimer()
      .then((res) => { if (alive) setDisclaimerAcked(!!res.data?.acked); })
      .catch(() => { if (alive) setDisclaimerAcked(false); });
    return () => { alive = false; };
  }, []);

  // Preferência de alerta de desconexão: busca UMA vez na montagem (só faz sentido pro dono).
  useEffect(() => {
    if (!isAdmin) return;
    let alive = true;
    getWzapNotifySettings()
      .then((res) => { if (alive) setNotifyOnDisconnect(res.data?.notify_on_disconnect !== false); })
      .catch(() => { /* silencioso — mantém o default ligado */ });
    return () => { alive = false; };
  }, [isAdmin]);

  const toggleNotifyOnDisconnect = async (next) => {
    setNotifyOnDisconnect(next);  // otimista
    setNotifySaving(true);
    try {
      await setWzapNotifySettings({ notify_on_disconnect: next });
      toast.success(next ? 'Avisos de desconexão ativados.' : 'Avisos de desconexão desativados.');
    } catch {
      setNotifyOnDisconnect(!next);  // reverte
      toast.error('Não foi possível salvar a preferência. Tente novamente.');
    } finally {
      setNotifySaving(false);
    }
  };

  // Tick de 1s só na tela de ativação — anima a barra de contagem regressiva (6h).
  useEffect(() => {
    if (patConfigured) return;
    const id = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(id);
  }, [patConfigured]);

  // Animação de entrada (controlada por JS, frame a frame — respeita a duração definida):
  // a barra "desce" do cheio (100%) até o tempo restante real, com faíscas na ponta.
  useEffect(() => {
    if (patConfigured) { setSprinkling(false); setBarW(100); return; }
    const TOTAL = 6 * 60 * 60 * 1000; // 6 horas
    const start = activationStartedAt ? new Date(activationStartedAt).getTime() : null;
    const hasStart = start != null && !Number.isNaN(start);
    const targetRemaining = hasStart ? Math.max(0, TOTAL - (Date.now() - start)) : TOTAL;
    const targetPct = (targetRemaining / TOTAL) * 100;
    const DURATION = 10000; // duração da descida (ms) — ajuste aqui pra mais lento/rápido
    const from = 100;
    const t0 = performance.now();
    const ease = (t) => 1 - Math.pow(1 - t, 3); // easeOutCubic
    let raf;
    setSprinkling(true);
    setBarW(from);
    const step = (now) => {
      const t = Math.min(1, (now - t0) / DURATION);
      setBarW(from + (targetPct - from) * ease(t));
      if (t < 1) {
        raf = requestAnimationFrame(step);
      } else {
        setSprinkling(false);
      }
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [patConfigured, activationStartedAt]);

  const handleAckDisclaimer = async () => {
    setAcking(true);
    try {
      await ackWhatsAppDisclaimer();
      setDisclaimerAcked(true);
    } catch {
      toast.error('Não foi possível registrar a confirmação. Tente novamente.');
    } finally {
      setAcking(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    const ok = await pollStatus(false);
    // Antes o toast dizia "Dados atualizados" mesmo quando tudo falhou (o pollStatus engolia o
    // erro), então o usuário esperava e recebia uma confirmação falsa.
    if (ok) toast.success('Dados atualizados');
    else toast.error('Não foi possível consultar a conexão agora.');
    setRefreshing(false);
  };

  const translateStatus = (status) => {
    const translations = {
      'online': 'Online', 'offline': 'Offline', 'operative': 'Operacional',
      'authorize': 'Aguardando Autorização', 'not_configured': 'Não Configurado',
      'initializing': 'Inicializando', 'connecting': 'Reconectando',
      'connected': 'Conectado', 'disconnected': 'Desconectado',
      'need_scan': 'Aguardando QR Code', 'unknown': 'Desconhecido',
      'logged_out': 'Desconectado',
      'error': 'Erro', 'pending': 'Pendente', 'loading': 'Carregando',
      'ready': 'Pronto', 'authenticated': 'Autenticado',
      'unauthenticated': 'Não Autenticado', 'qr': 'Aguardando QR Code',
      'healthy': 'Operacional', 'provider_unreachable': 'Servidor indisponível',
    };
    return translations[status] || status || 'Desconhecido';
  };

  // Formata o número conectado (só dígitos vindos do backend) para exibição.
  // Ex.: "5522981302523" -> "+55 (22) 98130-2523"; formatos fora do padrão BR caem no fallback.
  const formatPhone = (digits) => {
    const d = String(digits || '').replace(/\D/g, '');
    if (!d) return '';
    if (d.startsWith('55') && (d.length === 12 || d.length === 13)) {
      const ddd = d.slice(2, 4);
      const rest = d.slice(4);
      const mid = rest.length === 9 ? rest.slice(0, 5) : rest.slice(0, 4);
      const end = rest.length === 9 ? rest.slice(5) : rest.slice(4);
      return `+55 (${ddd}) ${mid}-${end}`;
    }
    return `+${d}`;
  };

  const st = getStatus(session, health);
  const providerDown = st.providerDown || backendDown;
  const isConnected = st.connected && !providerDown;
  const needsQR = st.needsQR && !providerDown;
  const transitional = st.transitional && !providerDown;
  const sessionStatus = session?.status || '';
  const healthStatus = health?.status || '';
  const connectedNumber = health?.number || '';

  if (loading) {
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card><CardContent className="p-6">
          <Skeleton className="h-8 w-48 mb-4" />
          <Skeleton className="h-64 w-64 mx-auto" />
        </CardContent></Card>
      </div>
    );
  }

  // PAT not yet configured by Super Admin - show "account being activated" message
  if (!patConfigured) {
    // Prazo estimado (6h) calculado aqui fora também: condiciona os textos abaixo da caixa
    // âmbar (quando expira, some o "até 6 horas" e o rodapé, que contradiriam/duplicariam a caixa).
    const ACT_TOTAL = 6 * 60 * 60 * 1000;
    const actStart = activationStartedAt ? new Date(activationStartedAt).getTime() : null;
    const actHasStart = actStart != null && !Number.isNaN(actStart);
    const actExpired = actHasStart && nowTs - actStart >= ACT_TOTAL;
    return (
      <div className="max-w-2xl mx-auto space-y-6">
        <Card data-testid="whatsapp-pending-activation">
          <CardHeader>
            <div className="flex flex-col items-center text-center gap-3">
              <div className="p-3 rounded-full bg-gray-100 dark:bg-gray-800">
                <Clock className="w-6 h-6 text-gray-700 dark:text-gray-300" />
              </div>
              <div className="space-y-1">
                <CardTitle className="text-2xl">Conta em Ativação</CardTitle>
                <CardDescription className="text-base">
                  Estamos preparando sua conexão com o WhatsApp.
                </CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="text-center py-12">
            {(() => {
              const TOTAL = ACT_TOTAL; // 6 horas (calculado acima, junto do actExpired)
              const hasStart = actHasStart;
              const remaining = hasStart ? Math.max(0, TOTAL - (nowTs - actStart)) : TOTAL;
              const expired = actExpired;

              // Prazo estimado esgotado: remove a barra e mostra uma mensagem simpática.
              if (expired) {
                return (
                  <div className="max-w-xl mx-auto -mt-6 mb-6 rounded-lg border border-amber-200 dark:border-amber-900/40 bg-amber-50/60 dark:bg-amber-950/20 p-4 text-base text-amber-800 dark:text-amber-300 text-center">
                    <p className="mb-2">
                      A ativação está levando mais tempo que o previsto.
                      <br />
                      Sua conta está <strong>na fila</strong> e será conectada assim que houver vaga.
                    </p>
                    <p>
                      Você <strong>não precisa fazer nada</strong>: o QR Code aparece aqui
                      automaticamente assim que a conexão for liberada.
                    </p>
                  </div>
                );
              }

              const pct = (remaining / TOTAL) * 100;
              const h = Math.floor(remaining / 3600000);
              const m = Math.floor((remaining % 3600000) / 60000);
              const s = Math.floor((remaining % 60000) / 1000);
              return (
                <div className="max-w-md mx-auto mb-6 -mt-4">
                  <div className="relative">
                    <div className="h-4 w-full rounded-full bg-emerald-100 dark:bg-emerald-950/30">
                      <div
                        className="bar-shimmer relative overflow-hidden h-full rounded-full bg-gradient-to-r from-emerald-400 to-green-500"
                        style={{ width: `${barW}%` }}
                      />
                    </div>
                    {sprinkling && (
                      <div
                        className="pointer-events-none absolute top-1/2"
                        style={{ left: `${barW}%` }}
                      >
                        {TIP_SPARKS.map((s, i) => (
                          <span
                            key={i}
                            className="bar-spark"
                            style={{
                              left: `${s.dx}px`,
                              width: `${s.size}px`,
                              height: `${s.size}px`,
                              backgroundColor: s.color,
                              animationDelay: s.delay,
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                  <div className="flex items-center justify-between text-base mt-2 text-emerald-700 dark:text-emerald-400">
                    <span className="font-medium">Tempo estimado restante</span>
                    <span className="font-semibold tabular-nums">
                      {hasStart ? `${h}h ${String(m).padStart(2, '0')}min ${String(s).padStart(2, '0')}s` : 'até 6h'}
                    </span>
                  </div>
                </div>
              );
            })()}
            <h3 className="text-xl font-semibold mb-3 mt-10">Sua conta está na fila de ativação</h3>
            {!actExpired && (
              <p className="text-muted-foreground max-w-md mx-auto mb-2">
                Esse processo costuma levar até <strong>6 horas</strong> e normalmente termina bem antes.
              </p>
            )}
            <p className="text-sm text-muted-foreground max-w-md mx-auto">
              Assim que sua conexão estiver pronta, você verá o QR Code aqui para escanear com seu WhatsApp.
            </p>
            {!actExpired && (
              <div className="mt-8 flex items-center justify-center gap-2 text-sm text-muted-foreground bg-secondary/50 rounded-lg p-3 max-w-md mx-auto">
                <Clock className="w-4 h-4" />
                <span>Você não precisa fazer nada, apenas aguardar.</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      {/* Status Card */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <Smartphone className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <CardTitle>Status da Conexão</CardTitle>
                <CardDescription>Status da sessão do WhatsApp</CardDescription>
              </div>
            </div>
            <Badge
              className={isConnected ? 'bg-teal-100 text-teal-800' : providerDown ? 'bg-slate-200 text-slate-800' : transitional ? 'bg-blue-100 text-blue-800' : needsQR ? 'bg-amber-100 text-amber-800' : 'bg-slate-200 text-slate-800'}
              data-testid="whatsapp-status-badge"
            >
              {isConnected ? (
                <><CheckCircle className="w-3 h-3 mr-1" /> Online</>
              ) : providerDown ? (
                <><CloudOff className="w-3 h-3 mr-1" /> Indisponível</>
              ) : transitional ? (
                <><RotateCw className="w-3 h-3 mr-1 animate-spin" /> Reconectando</>
              ) : needsQR ? (
                <><QrCode className="w-3 h-3 mr-1" /> Aguardando QR</>
              ) : (
                <><XCircle className="w-3 h-3 mr-1" /> Offline</>
              )}
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-3 gap-4">
            <div className="p-4 bg-secondary/50 rounded-lg text-center">
              <p className="text-sm text-muted-foreground">Status da Sessão</p>
              <p className="font-semibold">
                {providerDown ? 'Servidor indisponível' : translateStatus(sessionStatus)}
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg text-center">
              <p className="text-sm text-muted-foreground">Número conectado</p>
              <p className="font-semibold">
                {isConnected && connectedNumber ? formatPhone(connectedNumber) : '—'}
              </p>
            </div>
            <div className="p-4 bg-secondary/50 rounded-lg whitespace-nowrap text-center">
              <p className="text-sm text-muted-foreground">Saúde do número</p>
              {/* sem nível (ex.: provedor mudo) cai no "—": melhor que uma bolinha sem cor nem rótulo */}
              {numberHealth?.level ? (() => {
                const s = {
                  green:  { dot: 'bg-green-500', text: 'text-green-700 dark:text-green-400', label: 'Muito bom' },
                  yellow: { dot: 'bg-amber-500', text: 'text-amber-700 dark:text-amber-400', label: 'Razoável' },
                  red:    { dot: 'bg-red-500',   text: 'text-red-700 dark:text-red-400',     label: 'Ruim' },
                }[numberHealth.level] || {};
                const total = (numberHealth.sent_24h || 0) + (numberHealth.failed_24h || 0);
                return (
                  <TooltipProvider delayDuration={150}>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <div className="flex items-center justify-center gap-2 cursor-help">
                          <span className={`inline-block h-2.5 w-2.5 rounded-full ${s.dot}`} />
                          <span className={`font-semibold ${s.text}`}>{s.label}</span>
                        </div>
                      </TooltipTrigger>
                      <TooltipContent className="max-w-xs p-3 text-[13px] leading-relaxed shadow-lg ring-1 ring-black/10 dark:ring-white/15">
                        <p>{numberHealth.reason}.</p>
                        {total > 0 && (
                          <p className="mt-1">
                            Últimas 24h: {numberHealth.sent_24h} enviada(s)
                            {numberHealth.failed_24h > 0 ? `, ${numberHealth.failed_24h} falharam` : ''}.
                          </p>
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </TooltipProvider>
                );
              })() : (
                <p className="font-semibold">—</p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* QR Code Card */}
      <Card data-testid="whatsapp-qr-card">
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-gray-100 dark:bg-gray-800">
                <QrCode className="w-5 h-5 text-gray-700 dark:text-gray-300" />
              </div>
              <div>
                <CardTitle>Conectar WhatsApp</CardTitle>
                <CardDescription>
              {isConnected
                ? 'Seu WhatsApp está conectado e pronto para enviar mensagens.'
                : providerDown
                ? 'O servidor de conexão não está respondendo.'
                : transitional
                ? 'Reconectando seu WhatsApp...'
                : 'Escaneie o QR Code com seu WhatsApp para conectar.'}
                </CardDescription>
              </div>
            </div>
            <TooltipProvider delayDuration={150}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <button
                    type="button"
                    className="shrink-0 text-muted-foreground hover:text-foreground transition-colors cursor-help"
                    aria-label="Como conectar?"
                  >
                    <HelpCircle className="w-5 h-5" />
                  </button>
                </TooltipTrigger>
                <TooltipContent className="max-w-xs p-3 text-[13px] leading-relaxed shadow-lg ring-1 ring-black/10 dark:ring-white/15">
                  <p className="font-semibold mb-1.5">Como conectar?</p>
                  <ol className="list-decimal list-inside space-y-1">
                    <li>Abra o WhatsApp no seu celular</li>
                    <li>Toque em Configurações {'>'} Dispositivos Conectados</li>
                    <li>Toque em "Conectar um dispositivo"</li>
                    <li>Escaneie o QR Code exibido na tela</li>
                  </ol>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        </CardHeader>
        <CardContent className="flex flex-col items-center gap-4">
          {isConnected ? (
            <div className="text-center py-8">
              {/* Check de sucesso animado: círculo faz "pop" e o traço se desenha (one-shot na montagem). */}
              <style>{`
                @keyframes wa-check-pop {
                  0%   { transform: scale(0.5); opacity: 0; }
                  60%  { transform: scale(1.08); opacity: 1; }
                  100% { transform: scale(1); }
                }
                @keyframes wa-check-draw { to { stroke-dashoffset: 0; } }
                @keyframes wa-check-ring { to { stroke-dashoffset: 0; } }
                .wa-check-pop { animation: wa-check-pop 0.4s cubic-bezier(0.22, 1, 0.36, 1) both; }
                .wa-check-draw {
                  stroke-dasharray: 24;
                  stroke-dashoffset: 24;
                  animation: wa-check-draw 0.4s ease-out 0.25s forwards;
                }
                .wa-check-ring {
                  stroke-dasharray: 239;
                  stroke-dashoffset: 239;
                  animation: wa-check-ring 0.5s ease-out 0.65s forwards;
                }
                @media (prefers-reduced-motion: reduce) {
                  .wa-check-pop { animation: none; }
                  .wa-check-draw { animation: none; stroke-dashoffset: 0; }
                  .wa-check-ring { animation: none; stroke-dashoffset: 0; }
                }
              `}</style>
              <div className="wa-check-pop relative w-20 h-20 rounded-full bg-teal-100 flex items-center justify-center mx-auto mb-4">
                {/* Anel que se desenha na borda, na cor do check, logo após o traço terminar. */}
                <svg
                  className="absolute inset-0 w-full h-full -rotate-90 text-teal-600"
                  viewBox="0 0 80 80"
                  fill="none"
                  aria-hidden="true"
                >
                  <circle
                    className="wa-check-ring"
                    cx="40"
                    cy="40"
                    r="38"
                    stroke="currentColor"
                    strokeWidth={3}
                    strokeLinecap="round"
                  />
                </svg>
                <svg
                  className="w-10 h-10 text-teal-600"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth={2.5}
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  aria-hidden="true"
                >
                  <path className="wa-check-draw" d="M20 6 9 17l-5-5" />
                </svg>
              </div>
              <p className="text-lg font-semibold text-teal-700">WhatsApp Conectado!</p>
              <p className="text-sm text-muted-foreground mt-1">
                Você pode enviar mensagens normalmente.
              </p>
            </div>
          ) : providerDown ? (
            /* Pane NOSSA: nada de QR, nada de "tente novamente" — não há ação do usuário que
               resolva. O texto também não pode dizer que a conexão dele caiu, porque não caiu. */
            <div className="text-center py-8" data-testid="whatsapp-provider-down-state">
              <div className="w-20 h-20 rounded-full bg-slate-100 dark:bg-slate-800 flex items-center justify-center mx-auto mb-4">
                <CloudOff className="w-10 h-10 text-slate-500" />
              </div>
              <p className="text-lg font-semibold text-slate-700 dark:text-slate-300">
                Servidor de conexão indisponível
              </p>
              <p className="text-sm text-muted-foreground mt-2 max-w-md mx-auto">
                O servidor que mantém a conexão do WhatsApp não está respondendo.
                Sua sessão não foi encerrada e você não precisa parear o número de novo.
                Esta tela volta sozinha assim que o servidor responder.
              </p>
            </div>
          ) : transitional ? (
            <div className="text-center py-8" data-testid="whatsapp-connecting-state">
              <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mx-auto mb-4">
                <RotateCw className="w-10 h-10 text-blue-600 animate-spin" />
              </div>
              <p className="text-lg font-semibold text-blue-700">Reconectando...</p>
              <p className="text-sm text-muted-foreground mt-1">
                Sua conexão caiu e estamos tentando restabelecê-la automaticamente. Se não voltar em alguns segundos, mostraremos o QR Code para você reconectar.
              </p>
            </div>
          ) : (
            <>
              <ConnectDisclaimer acked={disclaimerAcked} acking={acking} onAck={handleAckDisclaimer} />
              {disclaimerAcked !== true ? (
                <div className="text-center py-1">
                  {disclaimerAcked === null ? (
                    <Loader2 className="w-6 h-6 text-muted-foreground animate-spin mx-auto" />
                  ) : (
                    <p className="text-sm text-muted-foreground">Confirme a ciência acima para ver o QR Code.</p>
                  )}
                </div>
              ) : typeof qrData?.qr === 'string' ? (
            <div className="qr-container bg-white p-4 rounded-lg shadow">
              <img
                src={`https://api.qrserver.com/v1/create-qr-code/?size=256x256&data=${encodeURIComponent(qrData.qr)}`}
                alt="QR Code"
                className="w-56 h-56"
                data-testid="whatsapp-qr-image"
              />
              <p className="text-xs text-muted-foreground text-center mt-2">
                Escaneie com seu WhatsApp
              </p>
            </div>
          ) : qrData?.status === 'connected' ? (
            <div className="text-center py-8">
              <CheckCircle className="w-16 h-16 text-teal-600 mx-auto mb-4" />
              <p className="text-lg font-semibold">Sessão já conectada</p>
            </div>
          ) : (
            <div className="text-center py-8">
              <p className="text-muted-foreground">QR Code não disponível</p>
              <p className="text-sm text-muted-foreground mt-1">
                Aguarde alguns minutos e tente novamente.
              </p>
            </div>
              )}
            </>
          )}

          <Button
            variant="secondary"
            onClick={handleRefresh}
            disabled={refreshing}
            data-testid="whatsapp-refresh-button"
          >
            {refreshing ? (
              <Loader2 className="w-4 h-4 mr-2 animate-spin" />
            ) : (
              <RefreshCw className="w-4 h-4 mr-2" />
            )}
            Atualizar Status
          </Button>

          {isAdmin && isConnected && (
            <div className="w-full flex items-start justify-between gap-4 rounded-lg border p-4">
              <div className="flex items-start gap-3">
                <Bell className="w-5 h-5 mt-0.5 text-muted-foreground shrink-0" />
                <div>
                  <p className="font-medium text-sm">Avisar por e-mail se o número desconectar</p>
                  <p className="text-sm text-muted-foreground">
                    Enviado a você e aos demais usuários com perfil Administrador da conta.
                  </p>
                </div>
              </div>
              <Switch
                checked={notifyOnDisconnect}
                onCheckedChange={toggleNotifyOnDisconnect}
                disabled={notifySaving}
                aria-label="Avisar por e-mail se o número desconectar"
              />
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
